export const prefectures: Record<string, string> = {
  hokkaido: "北海道",
  aomori: "青森",
  iwate: "岩手",
  miyagi: "宮城",
  akita: "秋田",
  yamagata: "山形",
  fukushima: "福島",
  ibaraki: "茨城",
  tochigi: "栃木",
  gunma: "群馬",
  saitama: "埼玉",
  chiba: "千葉",
  tokyo: "東京",
  kanagawa: "神奈川",
  niigata: "新潟",
  toyama: "富山",
  ishikawa: "石川",
  fukui: "福井",
  yamanashi: "山梨",
  nagano: "長野",
  gifu: "岐阜",
  shizuoka: "静岡",
  aichi: "愛知",
  mie: "三重",
  shiga: "滋賀",
  kyoto: "京都",
  osaka: "大阪",
  hyogo: "兵庫",
  nara: "奈良",
  wakayama: "和歌山",
  tottori: "鳥取",
  shimane: "島根",
  okayama: "岡山",
  hiroshima: "広島",
  yamaguchi: "山口",
  tokushima: "徳島",
  kagawa: "香川",
  ehime: "愛媛",
  kochi: "高知",
  fukuoka: "福岡",
  saga: "佐賀",
  nagasaki: "長崎",
  kumamoto: "熊本",
  oita: "大分",
  miyazaki: "宮崎",
  kagoshima: "鹿児島",
  okinawa: "沖縄",
  other: "その他",
};

export const genders: Record<string, string> = {
  man: "男子",
  woman: "女子",
};

// 年代の翻訳マッピング
export const ageGroups: Record<string, string> = {
  under_12: "U-12",
  under_15: "U-15",
  under_18: "U-18",
  under_22: "U-22",
  over_23: "O-23",
  over_30: "O-30",
  over_40: "O-40",
  over_50: "O-50",
  over_60: "O-60",
};

// プレイヤーレベル
export const playerLevels: Record<string, string> = {
  newbie: "初心者",
  beginner: "初級者",
  intermediate: "中級者",
  advanced: "上級者",
  semi_pro: "全国大会出場",
  pro: "プロ選手",
};

// 日付フォーマット関数
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("ja-JP", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    timeZone: "Asia/Tokyo",
  });
};

export const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString("ja-JP", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Tokyo",
  });
};

export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString("ja-JP", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Tokyo",
  });
};

// datetime-localフィールド用の値変換関数
export const toDateTimeLocalValue = (dateString: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);

  // Asia/Tokyoタイムゾーンでの日時を取得
  const tokyoDate = new Date(date.toLocaleString("en-US", { timeZone: "Asia/Tokyo" }));

  // YYYY-MM-DDTHH:mm形式に変換
  const year = tokyoDate.getFullYear();
  const month = String(tokyoDate.getMonth() + 1).padStart(2, "0");
  const day = String(tokyoDate.getDate()).padStart(2, "0");
  const hours = String(tokyoDate.getHours()).padStart(2, "0");
  const minutes = String(tokyoDate.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// debounce関数
export const debounce = <T extends (...args: any[]) => void>(func: T, delay: number): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// 生年月日から年齢グループを計算する関数（学年基準：4月2日〜翌年4月1日）
export const calculateAgeGroup = (year: number, month: number, day: number): string | null => {
  if (!year || !month || !day) return null;

  const today = new Date();
  const APRIL = 3;

  // 学年の基準年を計算（4月1日以前なら前年度、4月2日以降なら今年度）
  const currentMonth = today.getMonth();
  const currentDay = today.getDate();
  let schoolYear = today.getFullYear();
  if (currentMonth < APRIL || (currentMonth === APRIL && currentDay === 1)) {
    schoolYear -= 1; // 4月1日以前は前年度
  }

  // 生年月日の学年年度を計算
  const birthMonth = month - 1; // 0ベースに変換
  let birthSchoolYear = year;
  if (birthMonth < APRIL || (birthMonth === APRIL && day === 1)) {
    birthSchoolYear -= 1; // 4月1日以前生まれは前年度
  }

  // 学年差を計算（現在の学年年度 - 入学年度）
  const schoolAge = schoolYear - birthSchoolYear;

  if (schoolAge >= 0 && schoolAge <= 11) return ageGroups.under_12;
  if (schoolAge >= 12 && schoolAge <= 14) return ageGroups.under_15;
  if (schoolAge >= 15 && schoolAge <= 17) return ageGroups.under_18;
  if (schoolAge >= 18 && schoolAge <= 22) return ageGroups.under_22;
  if (schoolAge >= 23 && schoolAge <= 29) return ageGroups.over_23;
  if (schoolAge >= 30 && schoolAge <= 39) return ageGroups.over_30;
  if (schoolAge >= 40 && schoolAge <= 49) return ageGroups.over_40;
  if (schoolAge >= 50 && schoolAge <= 59) return ageGroups.over_50;
  if (schoolAge >= 60) return ageGroups.over_60;

  return null;
};

// 初期レーティングを計算する関数
export const calculateInitialRating = (ageGroup: string | null, gender: string, playerLevel: string): number | null => {
  if (!ageGroup || !gender || !playerLevel) return null;

  // 基本ベースレーティング（男子O23中級者をベース）
  let rating = 1500;

  // 年齢グループによる補正
  const ageAdjustments: Record<string, number> = {
    "under_12": -600,
    "under_15": -300,
    "under_18": 0,
    "under_22": 0,
    "over_23": 0,
    "over_30": -100,
    "over_40": -200,
    "over_50": -300,
    "over_60": -300,
  };

  // プレイヤーレベルによる補正
  const levelAdjustments: Record<string, number> = {
    "newbie": -600,      // 初心者
    "beginner": -300,    // 初級者
    "intermediate": 0,   // 中級者
    "advanced": 300,     // 上級者
    "semi_pro": 600,     // 全国大会レベル
    "pro": 900,          // プロ選手
  };

  // 女子の調整値
  const femaleSpecialAdjustments: Record<string, number> = {
    "under_12": 0,
    "under_15": -200,
    "under_18": -300,
    "under_22": -300,
    "over_23": -300,
    "over_30": -300,
    "over_40": -300,
    "over_50": -300,
    "over_60": -300,
  };

  // 表示値から内部keyに変換
  const ageGroupKey = Object.keys(ageGroups).find(key => ageGroups[key] === ageGroup);

  // 年齢グループ補正を適用
  if (ageGroupKey && ageAdjustments[ageGroupKey] !== undefined) {
    rating += ageAdjustments[ageGroupKey];
  }

  // プレイヤーレベル補正を適用
  if (levelAdjustments[playerLevel] !== undefined) {
    rating += levelAdjustments[playerLevel];
  }

  // 性別による補正（女子は年齢グループごとに調整）
  if (gender === "woman") {
    if (ageGroupKey && femaleSpecialAdjustments[ageGroupKey] !== undefined) {
      rating += femaleSpecialAdjustments[ageGroupKey];
    }
  }

  return rating;
};
